import React, { useRef, useEffect, useState, useCallback } from 'react';
import DraggableOverlay from './DraggableOverlay';
import ImagePreviewCrop from './ImagePreviewCrop';
import { ImageCrop } from './ImageCropBox';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Caption {
  id: string;
  text: string;
  start_time: number;
  end_time: number;
  style: Record<string, any>;
}

/** The segment fields playback has to honour. */
interface PreviewSegment {
  id: string;
  index: number;
  startTime: number;
  endTime: number;
  /** null means the segment carries no level of its own and takes the clip's. */
  volume: number | null;
  muted?: boolean;
  speed?: number | null;
  transition?: string;
}

/** Each transition's own length, matching the labels the transition picker advertises. */
const TRANSITION_SECONDS: Record<string, number> = {
  fade: 0.3,
  dissolve: 0.5,
  'slide-left': 0.3,
  'slide-right': 0.3,
  'zoom-in': 0.5,
  wipe: 0.3,
};

/**
 * Draws only `crop` of the source inside the overlay's box, matching the render's
 * `crop=` -> `scale=` chain: the whole image is blown up so the cropped part fills the box,
 * then slid so that part is what shows. An uncropped image just fills the box.
 */
function imageCropStyle(crop?: ImageCrop | null): React.CSSProperties {
  const c = crop;
  if (!c || (c.x <= 0 && c.y <= 0 && c.w >= 1 && c.h >= 1)) {
    return { width: '100%', height: '100%', display: 'block', pointerEvents: 'none' };
  }
  const w = Math.max(0.01, c.w);
  const h = Math.max(0.01, c.h);
  return {
    position: 'absolute',
    width: `${100 / w}%`,
    height: `${100 / h}%`,
    left: `${(-c.x / w) * 100}%`,
    top: `${(-c.y / h) * 100}%`,
    display: 'block',
    pointerEvents: 'none',
  };
}

interface VideoPreviewProps {
  fileUrl: string;
  /** True when fileUrl is the whole source video — the clip is a window inside it. */
  previewIsSource?: boolean;
  clipStart?: number;
  clipEnd?: number;
  captions: Caption[];
  currentTime: number;
  onTimeUpdate: (time: number) => void;
  onDurationChange: (dur: number) => void;
  aspectRatio: string;
  filterCSS?: string;
  crop?: { x: number; y: number; w: number; h: number };
  overlays?: { id: string; type: string; content: string; startTime: number; endTime: number; x: number; y: number; scale: number }[];
  images?: { id: string; imageUrl: string; src: string; x: number; y: number; width: number; height: number; opacity: number; rotation: number; startTime: number; endTime: number | null; layer: string; crop?: ImageCrop; segmentIds?: string[] }[];
  selectedOverlayId?: string | null;
  onOverlaySelect?: (id: string | null) => void;
  onOverlayMove?: (id: string, x: number, y: number) => void;
  onOverlayScale?: (id: string, scale: number) => void;
  onOverlayDelete?: (id: string) => void;
  selectedImageId?: string | null;
  onImageSelect?: (id: string | null) => void;
  onImageMove?: (id: string, x: number, y: number) => void;
  onImageResize?: (id: string, width: number, height: number, x: number, y: number) => void;
  /** The image being cropped on the frame, if any; its box turns into the crop rect. */
  croppingImageId?: string | null;
  onImageCrop?: (id: string, crop: ImageCrop) => void;
  onImageCropDone?: () => void;
  onImageRotate?: (id: string, rotation: number) => void;
  onImageDelete?: (id: string) => void;
  /** Segments, so playback can honour each one's mute, volume, speed and incoming transition. */
  segments?: PreviewSegment[];
  /** The clip's default level, used for a segment that carries none of its own. */
  masterVolume?: number;
  /** The clip's default speed, used for a segment that carries none of its own. */
  playbackSpeed?: number;
  /** Plays only [start, end] of the clip, looping when `loop` is set. */
  playRange?: { start: number; end: number; loop: boolean } | null;
  /** Bumped to (re)start playRange playback, so clicking play twice replays it. */
  playNonce?: number;
  /** Fired when a non-looping playRange reaches its end. */
  onPlayRangeEnd?: () => void;
  /** Lets the crop handles sit proud of the frame edge instead of being clipped. */
  cropping?: boolean;
  /** Rendered inside the video rect, so overlays anchored with inset:0 land on the picture. */
  children?: React.ReactNode;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  fileUrl, previewIsSource, clipStart, clipEnd,
  captions, currentTime, onTimeUpdate, onDurationChange, aspectRatio,
  filterCSS, crop, overlays, images,
  selectedOverlayId, onOverlaySelect, onOverlayMove, onOverlayScale, onOverlayDelete,
  selectedImageId, onImageSelect, onImageMove, onImageResize, onImageRotate, onImageDelete,
  croppingImageId, onImageCrop, onImageCropDone,
  segments, masterVolume = 1, playbackSpeed = 1,
  playRange, playNonce, onPlayRangeEnd,
  cropping, children,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  // Read inside the timeupdate listener, which is registered once — a prop would go stale there.
  const playRangeRef = useRef(playRange);
  /**
   * The preview plays the edit, not the source file: segments run in the order the timeline
   * holds them, and material outside them is never played. `playPosRef` is the position in that
   * order the playhead is currently in, so the end of one segment can hand over to the next even
   * when the next one starts earlier in the source.
   */
  const segmentsRef = useRef(segments);
  useEffect(() => { segmentsRef.current = segments; }, [segments]);
  const playPosRef = useRef(0);
  const onPlayRangeEndRef = useRef(onPlayRangeEnd);
  useEffect(() => { playRangeRef.current = playRange; }, [playRange]);
  useEffect(() => { onPlayRangeEndRef.current = onPlayRangeEnd; }, [onPlayRangeEnd]);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  const activeSegment = (segments || []).find(
    sg => currentTime >= sg.startTime && currentTime < sg.endTime
  );

  // Mute, volume and speed belong to the segment being played. A segment that carries none of
  // its own takes the clip's — the same chain the render resolves, so the two agree.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = activeSegment?.speed ?? playbackSpeed;
    v.muted = activeSegment?.muted ?? false;
    const level = volume * (activeSegment?.volume ?? masterVolume);
    v.volume = Math.max(0, Math.min(1, level));
  }, [
    activeSegment?.index, activeSegment?.speed, activeSegment?.muted, activeSegment?.volume,
    volume, masterVolume, playbackSpeed,
  ]);

  // A segment's incoming transition, played as the picture enters. Nothing is drawn for the
  // first segment — there is no cut before it — nor for a plain cut.
  const transitionStyle = (): React.CSSProperties => {
    const seg = activeSegment;
    if (!seg || !seg.transition || seg.transition === 'none' || seg.index === 0) return {};
    const span = TRANSITION_SECONDS[seg.transition];
    if (!span) return {};
    const p = (currentTime - seg.startTime) / span;
    if (p < 0 || p >= 1) return {};
    switch (seg.transition) {
      case 'fade':
      case 'dissolve':
        return { opacity: p };
      case 'slide-left':
        return { transform: `translateX(${(1 - p) * 100}%)` };
      case 'slide-right':
        return { transform: `translateX(${-(1 - p) * 100}%)` };
      case 'zoom-in':
        return { transform: `scale(${0.8 + 0.2 * p})`, opacity: p };
      case 'wipe':
        return { clipPath: `inset(0 ${(1 - p) * 100}% 0 0)` };
      default:
        return {};
    }
  };

  // While a single segment is playing, the transport belongs to that segment: the seek bar spans
  // it, the readouts count from its start, and the arrow keys cannot step outside it.
  const transportStart = playRange ? playRange.start : 0;
  const transportEnd = playRange ? playRange.end : duration;
  const transportRef = useRef({ start: transportStart, end: transportEnd });
  useEffect(() => {
    transportRef.current = { start: transportStart, end: transportEnd };
  }, [transportStart, transportEnd]);

  const activeCaption = captions.find(
    c => currentTime >= c.start_time && currentTime <= c.end_time
  );

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  }, []);

  // When the preview is the source video, the clip is the window [clipStart, clipEnd] inside it.
  const windowStart = previewIsSource ? (clipStart ?? 0) : 0;
  const windowEnd = previewIsSource ? clipEnd : undefined;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onLoaded = () => {
      const end = windowEnd && windowEnd > windowStart ? windowEnd : v.duration;
      const dur = Math.max(0, end - windowStart);
      setDuration(dur);
      onDurationChange(dur);
      if (windowStart > 0) v.currentTime = windowStart;
    };
    const onTime = () => {
      const pr = playRangeRef.current;
      if (pr && v.currentTime >= windowStart + pr.end) {
        if (pr.loop) {
          v.currentTime = windowStart + pr.start;
        } else {
          v.pause();
          setPlaying(false);
          onPlayRangeEndRef.current?.();
        }
        onTimeUpdate(v.currentTime - windowStart);
        return;
      }

      // Follow the edit: when the playhead runs off the end of the segment being played, hand
      // over to the next one in timeline order rather than letting the source run on through
      // material that was trimmed away or moved elsewhere.
      const segs = segmentsRef.current || [];
      if (!pr && segs.length > 0) {
        const t = v.currentTime - windowStart;
        const pos = Math.min(playPosRef.current, segs.length - 1);
        const cur = segs[pos];
        if (cur && t >= cur.endTime) {
          const next = pos + 1;
          if (next < segs.length) {
            playPosRef.current = next;
            v.currentTime = windowStart + segs[next].startTime;
          } else {
            playPosRef.current = 0;
            v.pause();
            setPlaying(false);
            v.currentTime = windowStart + segs[0].startTime;
          }
          onTimeUpdate(v.currentTime - windowStart);
          return;
        }
        if (cur && t < cur.startTime) {
          // The author seeked. Adopt whichever segment they landed in; a landing in trimmed-out
          // material moves on to the next segment that starts after it.
          const landed = segs.findIndex(sg => t >= sg.startTime && t < sg.endTime);
          if (landed >= 0) {
            playPosRef.current = landed;
          } else {
            const ahead = segs.findIndex(sg => sg.startTime > t);
            playPosRef.current = ahead >= 0 ? ahead : 0;
            v.currentTime = windowStart + segs[playPosRef.current].startTime;
            onTimeUpdate(v.currentTime - windowStart);
            return;
          }
        }
      }

      if (windowEnd && v.currentTime >= windowEnd) {
        v.pause();
        setPlaying(false);
        v.currentTime = windowStart;
      }
      onTimeUpdate(v.currentTime - windowStart);
    };
    const onEnd = () => setPlaying(false);
    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('ended', onEnd);
    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('ended', onEnd);
    };
  }, [onTimeUpdate, onDurationChange, windowStart, windowEnd]);

  // Clearing the range (toggling play/repeat off) stops playback; a range that simply
  // ran out has already paused itself.
  const hadRangeRef = useRef(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!playRange && hadRangeRef.current) { v.pause(); setPlaying(false); }
    hadRangeRef.current = !!playRange;
  }, [playRange]);

  // Start (or restart) range playback whenever the range or its nonce changes.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playRange) return;
    v.currentTime = windowStart + playRange.start;
    const p = v.play();
    if (p) p.catch(() => {});
    setPlaying(true);
  }, [playNonce, playRange?.start, playRange?.end, playRange?.loop, windowStart]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
      if (e.code === 'Space') { e.preventDefault(); togglePlay(); }
      const v = videoRef.current;
      if (!v) return;
      const { start, end } = transportRef.current;
      if (e.code === 'ArrowLeft') {
        v.currentTime = Math.max(windowStart + start, v.currentTime - 0.1);
      }
      if (e.code === 'ArrowRight') {
        v.currentTime = Math.min(windowStart + end, v.currentTime + 0.1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [togglePlay, windowStart]);

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    if (videoRef.current) videoRef.current.currentTime = windowStart + t;
    const landed = (segments || []).findIndex(sg => t >= sg.startTime && t < sg.endTime);
    if (landed >= 0) playPosRef.current = landed;
  };

  // The element's level is composed from monitor × segment × master, so only the state moves here.
  const changeVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value));
  };

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const aspectClass = aspectRatio === '1:1' ? 'aspect-1-1'
    : aspectRatio === '16:9' ? 'aspect-16-9' : 'aspect-9-16';

  // Filter visible overlays and images by current time
  const activeOverlays = (overlays || []).filter(
    o => currentTime >= o.startTime && currentTime <= o.endTime
  );
  // A segment-bound image is on screen exactly while one of *its* segments is playing. Its
  // start/end are only the envelope of that set, so testing against them would also show an
  // image bound to segments 1 and 3 all the way through segment 2. An unbound image still
  // takes its own times.
  const activeImages = (images || []).filter(img => {
    const bound = (img.segmentIds || []).filter(id => (segments || []).some(sg => sg.id === id));
    if (bound.length > 0) {
      return (segments || []).some(
        sg => bound.includes(sg.id) && currentTime >= sg.startTime && currentTime < sg.endTime
      );
    }
    return currentTime >= img.startTime && (img.endTime === null || currentTime <= img.endTime);
  });

  // Crop style
  const cropStyle: React.CSSProperties = {};
  if (crop && (crop.x > 0 || crop.y > 0 || crop.w < 1 || crop.h < 1)) {
    cropStyle.objectFit = 'cover';
    cropStyle.objectPosition = `${-crop.x / crop.w * 100}% ${-crop.y / crop.h * 100}%`;
    cropStyle.transform = `scale(${1 / crop.w}, ${1 / crop.h})`;
    cropStyle.transformOrigin = `${crop.x * 100}% ${crop.y * 100}%`;
  }

  /**
   * Clicking away from everything drops the selection. Only the bare frame and the picture
   * itself count as "away" — an overlay, a caption or the crop toolbar sitting on the frame is
   * a click *on* something, and the overlays stop their own clicks before they get here anyway.
   */
  const clearSelection = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    if (el !== e.currentTarget && el.tagName !== 'VIDEO') return;
    onOverlaySelect?.(null);
    onImageSelect?.(null);
    onImageCropDone?.();
  };

  return (
    <div className="video-preview-panel">
      {/* The stage fills the column like the Kinetic Caption preview; the frame inside stays
          locked to the clip's aspect ratio, so it remains the video rect the overlays sit on. */}
      <div className="video-preview-stage">
        <div
          className={`video-preview-frame ${aspectClass}${cropping ? ' video-preview-frame--cropping' : ''}`}
          onClick={clearSelection}
        >
        <video
          ref={videoRef}
          src={fileUrl.startsWith('http') ? fileUrl : `${API_BASE}${fileUrl}`}
          preload="auto"
          onClick={togglePlay}
          style={{ filter: filterCSS || 'none', ...cropStyle, ...transitionStyle() }}
        />

        {/* Image overlays (sorted by layer) */}
        {activeImages.sort((a, b) => {
          const order: Record<string, number> = { back: 0, middle: 1, front: 2 };
          return (order[a.layer] || 1) - (order[b.layer] || 1);
        }).map(img => (
          croppingImageId === img.id ? (
            <ImagePreviewCrop
              key={img.id}
              image={img}
              onChange={(crop) => onImageCrop?.(img.id, crop)}
              onDone={() => onImageCropDone?.()}
            />
          ) : (
          <DraggableOverlay
            key={img.id}
            x={img.x} y={img.y} scale={1}
            width={img.width} height={img.height}
            rotation={img.rotation}
            opacity={img.opacity}
            selected={selectedImageId === img.id}
            onSelect={() => onImageSelect?.(img.id)}
            onMove={(x, y) => onImageMove?.(img.id, x, y)}
            onScale={() => {}}
            onResize={(w, h, nx, ny) => onImageResize?.(img.id, w, h, nx, ny)}
            onRotate={(r) => onImageRotate?.(img.id, r)}
            onDelete={() => onImageDelete?.(img.id)}
          >
            <div className="image-overlay-viewport">
              <img
                src={img.src}
                alt="overlay"
                style={imageCropStyle(img.crop)}
                draggable={false}
              />
            </div>
          </DraggableOverlay>
          )
        ))}

        {/* Emoji overlays */}
        {activeOverlays.map(o => (
          <DraggableOverlay
            key={o.id}
            x={o.x} y={o.y} scale={o.scale}
            selected={selectedOverlayId === o.id}
            onSelect={() => onOverlaySelect?.(o.id)}
            onMove={(x, y) => onOverlayMove?.(o.id, x, y)}
            onScale={(s) => onOverlayScale?.(o.id, s)}
            onDelete={() => onOverlayDelete?.(o.id)}
          >
            <span className="emoji-overlay-content" style={{ fontSize: '2rem' }}>{o.content}</span>
          </DraggableOverlay>
        ))}

        {activeCaption && (
          <div className="video-caption-overlay">
            {activeCaption.text}
          </div>
        )}

        {children}
        </div>
      </div>

      <div className="video-controls">
        <button className="play-btn" onClick={togglePlay}>
          {playing ? '⏸' : '▶'}
        </button>
        <span className="time-display">{formatTime(currentTime - transportStart)}</span>
        <input
          type="range" className="seek-bar"
          min={transportStart} max={transportEnd} step={0.01}
          value={Math.min(Math.max(currentTime, transportStart), transportEnd)} onChange={seek}
        />
        <span className="time-display">{formatTime(transportEnd - transportStart)}</span>
        {playRange && (
          <span className="transport-scope" title="The transport is scoped to the playing segment">
            segment
          </span>
        )}
        <input
          type="range" className="volume-bar"
          min={0} max={1} step={0.01}
          value={volume} onChange={changeVolume}
        />
      </div>
    </div>
  );
};

export default VideoPreview;
